import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppLoader from '@nxsys/components/AppLoader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { aepnActions } from '@nxsys/store/slices/aepn/reducer/aepnSlice';
import { IContribution } from '@nxsys/types/models/aepn/contribution';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { showMessage } from '@nxsys/utils/message';
import { formatMoneyExport } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContributionsFilterModal, {
  ContributionsFilterValues,
} from '../components/ContributionsFilterModal';
import { getContributionsSubmittedColumns } from './contributions-columns';
import {
  ContributionsIrelandTabRef,
  CONTRIBUTIONS_IRELAND_TAB,
} from './ContributionsReportIreland';

type Props = {
  tabRef: React.RefObject<ContributionsIrelandTabRef>;
  tabType: CONTRIBUTIONS_IRELAND_TAB;
};

const ContributionsSubmittedTab: React.FC<Props> = ({
  tabRef,
  tabType,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const { payrollCountry, isBureauEmployer, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { contributionsHistory, actionLoading } = useSelector(
    (state: AppState) => state.aepnStore,
  );
  const { list, totalDocs, loading } = contributionsHistory;

  const currentTaxYear = getTaxWeekTaxYear().taxYear;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [queryParams, setQueryParams] = useState<Record<string, any>>({
    taxYear: currentTaxYear,
  });
  const [selectedRows, setSelectedRows] = useState<IContribution[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const isErrorTab = tabType === CONTRIBUTIONS_IRELAND_TAB.ERRORS;

  const fetchData = useCallback(
    (params: Record<string, any>) => {
      const merged = { ...queryParams, ...params };
      setQueryParams(merged);
      if (params.page !== undefined) setPage(params.page);
      const reqParams: Record<string, any> = { ...merged };
      if (isErrorTab) reqParams.isError = true;
      else reqParams.isError = undefined;
      if (isBureauEmployer && user?.companyUserIds?.length) {
        reqParams.companyIds = user.companyUserIds;
      } else {
        reqParams.companyIds = undefined;
      }
      dispatch(aepnActions.getContributionsHistory(reqParams) as any);
    },
    [queryParams, isErrorTab, isBureauEmployer, user],
  );

  useEffect(() => {
    fetchData({ page: 0, size: 20, taxYear: currentTaxYear });
  }, []);

  useImperativeHandle(tabRef, () => ({
    exportSelected: handleExportSelected,
    exportAll: handleExportAll,
    exportErrorsSelected: handleExportSelected,
    exportAllErrors: handleExportAll,
    resubmit: handleResubmit,
    markAsSubmitted: handleMarkAsSubmitted,
    checkStatus: handleCheckStatus,
  }));

  const buildExportRow = (item: IContribution) => ({
    ...(isErrorTab ? { error: item.errorDescription || '' } : {}),
    status: item.status || '',
    taxPeriod: item.taxPeriod ? `${item.taxPeriod}/${item.taxYear}` : '',
    employerRef: item.subcontractor?.agencyRef || '_',
    employeeName:
      item.subcontractor?.fullName ||
      `${item.subcontractor?.forename || ''}${item.subcontractor?.surname || ''}` ||
      '_',
    paymentCompany: item.submission?.company?.companyName || '',
    grossForPension: formatMoneyExport(item.grossPay, showCurrencyByCountry(payrollCountry)),
    employeeContribution: formatMoneyExport(item.eeContribution, showCurrencyByCountry(payrollCountry)),
    employerContribution: formatMoneyExport(item.erContribution, showCurrencyByCountry(payrollCountry)),
    totalContribution: formatMoneyExport(
      Number(item.eeContribution || 0) + Number(item.erContribution || 0),
      showCurrencyByCountry(payrollCountry),
    ),
  });

  const buildCsvHeaders = () => {
    const base = [
      'Status',
      'Tax Period',
      'Employer Ref',
      'Employee Name',
      isBureau ? 'Employer' : 'Payment Company',
      'Gross for Pension',
      'Employee Contribution',
      'Employer Contribution',
      'Total Contribution',
    ];
    return isErrorTab ? ['Error', ...base] : base;
  };

  const doExport = (data: IContribution[]) => {
    const csvExporter = new ExportToCsv({
      filename: `Pension-contributions-${isErrorTab ? 'errors-' : ''}${moment().format('DD-MM-YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers: buildCsvHeaders(),
    });
    try {
      csvExporter.generateCsv(data.map(buildExportRow));
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (!selectedRows.length) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    doExport(selectedRows);
  };

  const handleExportAll = async () => {
    if (!totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    const reqParams: Record<string, any> = { ...queryParams, page: 0, size: totalDocs };
    if (isErrorTab) reqParams.status = 'ERROR';
    if (isBureauEmployer && user?.companyUserIds?.length) {
      reqParams.companyIds = user.companyUserIds;
    }
    const result = await (dispatch(aepnActions.getContributionsHistory(reqParams) as any) as any).unwrap();
    doExport(result?.data || []);
  };

  const handleResubmit = async () => {
    if (!selectedRows.length) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    const submissionIds = selectedRows
      .map((r) => r.submissionId)
      .filter(Boolean) as string[];
    if (!submissionIds.length) {
      showMessage({ type: 'warning', content: 'No submission IDs found for selected rows.' });
      return;
    }
    try {
      await (dispatch(aepnActions.resubmitContributions({ submissionIds }) as any) as any).unwrap();
      showMessage({ type: 'info', content: 'Resubmitting contributions. Please wait...' });
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleMarkAsSubmitted = () => {
    showMessage({ type: 'info', content: 'Mark as submitted is not supported for Ireland contributions.' });
  };

  const handleCheckStatus = async () => {
    try {
      await (dispatch(aepnActions.getContributionsStatus() as any) as any).unwrap();
      showMessage({ type: 'info', content: 'Syncing status and errors. Please wait...' });
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        fetchData({ search: e.target.value, page: 0 }),
      500,
    ),
    [fetchData],
  );

  const handleFilter = (values: ContributionsFilterValues) => {
    fetchData({
      ...values,
      page: 0,
    });
  };

  const onChangePagination = (newPage: number, newSize: number) => {
    setPageSize(newSize);
    fetchData({ page: newPage - 1, size: newSize });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.payFrequency;

  return (
    <>
      {actionLoading && <AppLoader />}
      <AppsHeader>
        <div className="flex justify-between flex-wrap w-full gap-4">
          <div className="flex flex-wrap gap-4">
            <AppInputSearch onChange={handleSearch} />
            <AppMenuFilter
              options={[]}
              onChange={() => {}}
              onClick={() => setIsOpenFilterModal(true)}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <TaxYearSelect
              initValue={currentTaxYear}
              onFilter={(val) => fetchData({ taxYear: val.taxYear, page: 0 })}
            />
          </div>
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={page}
          total={totalDocs}
          pageSize={pageSize}
          scroll={{ x: 2200, y: 700 }}
          columns={getContributionsSubmittedColumns({
            messages,
            payrollCountry: (payrollCountry || '') as PayrollCountryType,
            isErrorTab,
            isBureau,
          })}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], rows: any[]) => setSelectedRows(rows),
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>
      <ContributionsFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </>
  );
};

export default ContributionsSubmittedTab;
